import {
    useTransition,
    useRef,
    useCallback,
    useState,
    useMemo,
    useEffect
} from "react";

import { graphql, useRefetchableFragment } from "react-relay";
import { Initializer } from "src/analytics/atl-analytics/initializer";

import {
    templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key,
    templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data
} from "@/__generated_contentful__/templatePaginatedSearchResultsContentfulRelayRefetchableFragment.graphql";

import { TemplateCardGrid } from "../../components/contentful-relay-components/template-card-grid-contentful-relay";

export const INITIAL_PAGE_SIZE = 9;

function hasPage(
    pages: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data[],
    page: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data
) {
    return pages.some((p) => {
        return (
            p.templates?.items?.length === page.templates?.items?.length &&
            p.templates?.total === page.templates?.total
        );
    });
}

type SortLookup = {
    [templateUrlSlug: string]: number;
};

function sortBy(
    // @ts-ignore
    items: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data["templates"]["items"],
    sortBy?: "templateNameExternal" | SortLookup | null
    // @ts-ignore
): templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data["templates"]["items"] {
    if (!sortBy) {
        return [...items];
    }
    if (sortBy === "templateNameExternal") {
        return [...items].sort((a, b) =>
            a.templateNameExternal && b.templateNameExternal
                ? a.templateNameExternal.localeCompare(b.templateNameExternal)
                : a.templateNameExternal
                ? -1
                : 1
        );
    }

    return [...items].sort((a, b) => {
        const aRank = a.urlSlug ? sortBy[a.urlSlug] : undefined;
        const bRank = b.urlSlug ? sortBy[b.urlSlug] : undefined;
        // Sort by rank 1 is higher than 2
        // if aRank is undefined, it should be sorted lower than bRank
        if (aRank === undefined) {
            return bRank === undefined ? 0 : 1;
        }
        if (bRank === undefined) {
            return -1;
        }
        return aRank - bRank;
    });
}

function dataToPages(
    data: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data,
    sortLookup?: "templateNameExternal" | SortLookup | null
): templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data[] {
    if (!data.templates?.items) {
        return [data];
    }

    const items = data.templates.items;
    if (items.length > INITIAL_PAGE_SIZE) {
        const sortedItems = sortBy(items, sortLookup);
        const initialItems = sortedItems.slice(0, INITIAL_PAGE_SIZE);
        const additionalItems = sortedItems.slice(INITIAL_PAGE_SIZE);
        return [
            {
                ...data,
                templates: {
                    ...data.templates,
                    items: initialItems
                }
            },
            {
                ...data,
                templates: {
                    ...data.templates,
                    items: additionalItems
                }
            }
        ];
    }

    return [data];
}

export type TemplatePaginatedSearchResultsProps = {
    initialSearchResults: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key;
    /**
     * `searchQuery` is required to be able to fetch additional pages
     * of results. Because CPAPI does not separate pagination
     * arguments from search arguments, it is not possible to query
     * pagination without reconstructing the entire query arguments.
     */
    urlSlugs: string[];
    templateIds: string[];
    categoryUrlSlugs: string[];
    limit: number;
    skip: number;
    locale: string;
    sortLookup?: "templateNameExternal" | SortLookup | null;
    wasPreloaded?: boolean;
};

export const TemplatePaginatedSearchResultsRelayRefetchableFragment = graphql`
    fragment templatePaginatedSearchResultsContentfulRelayRefetchableFragment on Query
    @refetchable(queryName: "templatePaginatedSearchResultsRefetchableQuery")
    @argumentDefinitions(
        urlSlugs: { type: "[String!]!" }
        templateIds: { type: "[String!]!" }
        categoryUrlSlugs: { type: "[String!]!" }
        limit: { type: "Int!" }
        skip: { type: "Int!" }
        locale: { type: "String!" }
    ) {
        templates: productTemplateCollection(
            limit: $limit
            skip: $skip
            locale: $locale
            where: {
                OR: [
                    { urlSlug_in: $urlSlugs }
                    { sys: { id_in: $templateIds } }
                    { category: { urlSlug_in: $categoryUrlSlugs } }
                ]
            }
        ) {
            items {
                sys {
                    id
                }
                productCollection(limit: 1) {
                    items {
                        productNameReference {
                            productNameId
                        }
                    }
                }
                templateNameExternal
                urlSlug
                ...templateCardContentfulRelayFragment
            }
            total
        }
    }
`;

// @ts-ignore
const filterConfluenceTemplates = (items) => {
    // @ts-ignore
    return items.filter((item) => {
        const productNameId =
            item.productCollection?.items?.[0]?.productNameReference
                ?.productNameId;
        return productNameId === "confluence";
    });
};

export const TemplatePaginatedSearchResults = ({
    initialSearchResults,
    urlSlugs,
    templateIds,
    categoryUrlSlugs,
    limit,
    skip,
    locale,
    sortLookup = "templateNameExternal",
    wasPreloaded = false
}: TemplatePaginatedSearchResultsProps) => {
    const [loadMorePending, startLoadMore] = useTransition();
    const [currentPage, setCurrentPage] = useState(1);

    const [data, refetchFragment] = useRefetchableFragment(
        TemplatePaginatedSearchResultsRelayRefetchableFragment,
        initialSearchResults
    );

    const [prevSearchParams, setPrevSearchParams] = useState({
        urlSlugs,
        templateIds,
        categoryUrlSlugs,
        limit,
        skip,
        locale
    });

    const [prevData, setPrevData] = useState(data);

    /** A ref to the latest refetch request. */
    const refetchRef = useRef<ReturnType<typeof refetchFragment>>();

    const [paginatedSearchResults, setPaginatedSearchResults] = useState(() =>
        dataToPages(data, sortLookup)
    );

    const resetSearchResults = useCallback(
        (
            data?: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data
        ) => {
            setCurrentPage(1);
            setPaginatedSearchResults(
                data ? dataToPages(data, sortLookup) : []
            );
        },
        [sortLookup]
    );

    const addSearchResults = useCallback(
        (
            data: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data
        ) => {
            const pagesToAdd = dataToPages(data, sortLookup).filter(
                (newPage) => !hasPage(paginatedSearchResults, newPage)
            );
            if (pagesToAdd.length > 0) {
                setPaginatedSearchResults((prev) => [...prev, ...pagesToAdd]);
            }
        },
        [paginatedSearchResults, sortLookup]
    );

    const [hasNextPage, setHasNextPage] = useState(
        currentPage < paginatedSearchResults.length
    );

    useEffect(() => {
        setHasNextPage(currentPage < paginatedSearchResults.length);
    }, [currentPage, paginatedSearchResults]);

    const currentSearchResults = useMemo(() => {
        const results = paginatedSearchResults.slice(0, currentPage).reduce(
            (acc, { templates }) => ({
                ...acc,
                // @ts-ignore
                items: [...acc.items, ...(templates?.items ?? [])],
                total: templates?.total ?? 0
            }),
            {
                items: [],
                total: 0
            } as templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data["templates"]
        );

        // Filter to only include Confluence templates
        const filteredResults = {
            ...results,
            // @ts-ignore
            items: filterConfluenceTemplates(results.items)
        };

        return filteredResults;
    }, [currentPage, paginatedSearchResults]);

    const loadMore = useCallback(() => {
        if (hasNextPage) {
            setCurrentPage((prev) => prev + 1);
            const nextSkip = (currentPage - 1) * limit;
            startLoadMore(() => {
                refetchRef.current?.dispose();
                refetchRef.current = refetchFragment({
                    urlSlugs,
                    templateIds,
                    categoryUrlSlugs,
                    limit: limit,
                    skip: nextSkip,
                    locale
                });
            });
        }
    }, [
        hasNextPage,
        currentPage,
        limit,
        refetchFragment,
        urlSlugs,
        templateIds,
        categoryUrlSlugs,
        locale
    ]);

    // Reset the search results when the search query changes,
    // or add search results when the data changes.
    useEffect(() => {
        const arraysEqual = (a?: string[], b?: string[]) =>
            a === b ||
            (!!a &&
                !!b &&
                a.length === b.length &&
                a.every((v, i) => v === b[i]));

        const searchParamsChanged =
            !arraysEqual(prevSearchParams.urlSlugs, urlSlugs) ||
            !arraysEqual(prevSearchParams.templateIds, templateIds) ||
            !arraysEqual(prevSearchParams.categoryUrlSlugs, categoryUrlSlugs) ||
            prevSearchParams.limit !== limit ||
            prevSearchParams.skip !== skip ||
            prevSearchParams.locale !== locale;

        if (searchParamsChanged) {
            setPrevSearchParams({
                urlSlugs,
                templateIds,
                categoryUrlSlugs,
                limit,
                skip,
                locale
            });
            setPrevData(data);
            resetSearchResults(data);
        } else if (prevData !== data) {
            setPrevData(data);
            addSearchResults(data);
        }
    }, [
        addSearchResults,
        data,
        prevData,
        prevSearchParams,
        resetSearchResults,
        urlSlugs,
        templateIds,
        categoryUrlSlugs,
        limit,
        skip,
        locale
    ]);

    // TODO: Figure out if this is the right place to do this.
    // Reinitialize tracking when displayed templates changes.
    useEffect(() => {
        Initializer.autoTrack();
    }, [currentSearchResults]);

    return (
        <TemplateCardGrid
            // @ts-ignore
            templates={currentSearchResults}
            hasNextPage={hasNextPage}
            onLoadMore={loadMore}
            initialAnimation={!wasPreloaded}
            loadMorePending={loadMorePending}
        />
    );
};
